import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useMemo } from 'react';

export const useScreenDimensions = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');

  return {
    isDesktop,
    isMobile: !isDesktop,
    isTouchDevice,
  };
};

export const Platform = (() => {
  let platform = '';
  const userAgent = navigator.userAgent.toLowerCase();

  if ('userAgentData' in navigator) {
    platform = window.navigator.userAgentData?.platform || '';
  } else {
    platform = window.navigator.userAgent;
  }

  const isMacOS = /mac/i.test(platform);
  const isWindows = /win/i.test(platform);

  // Safari detection: contains "safari" but not "chrome"/"android"/"crios"/"crmo"
  const isSafari = /safari/i.test(userAgent) && !/chrome|crios|crmo|android/i.test(userAgent);

  return {
    isMacOS,
    isWindows,
    isSafari,
    select: <T1, T2>({ macOS, windows }: { macOS: T1; windows: T2 }): T1 | T2 => {
      if (isMacOS) {
        return macOS;
      }
      return windows;
    },
  };
})();

export const useFormatShortcut = () => {
  const symbols = useMemo(
    () => ({
      up: '↑',
      down: '↓',
      left: '←',
      right: '→',
      ...(Platform.select({
        macOS: { command: '⌘', ctrl: '⌃', alt: '⌥', shift: '⇧' },
        windows: { command: 'Ctrl', ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift' },
      }) as Record<'command' | 'ctrl' | 'alt' | 'shift', string>),
    }),
    [],
  );

  return useCallback(
    (shortcut: string) => {
      return shortcut
        .toLowerCase()
        .split('+')
        .map((key) => symbols[key as keyof typeof symbols] || key.toUpperCase())
        .join('+');
    },
    [symbols],
  );
};
